<template>
  <v-page page="assessmentRescore" title="Assessment Rescore">
    <v-container class="px-0 mx-0 pt-1 mt-3">
      <v-layout>
        <v-flex>
          <v-layout mr-4 pr-4>
            <v-flex pt-1>
              <v-text-field v-model="query"
                            placeholder="Search"
                            clearable solo
                            @keydown.enter="search"
                            data-test="searchInput"></v-text-field>
            </v-flex>
            <v-flex pt-1 xs2 ml-2>
              <v-select v-model="searchAttribute"
                        :items="searchAttributeChoices"
                        @change="search" solo></v-select>
            </v-flex>
            <v-flex xs2 lg1 ml-2 mt-1>
              <v-btn large @click="search" color="primary">Search</v-btn>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex>
              <v-toolbar flat color="#FAFAFA" class="update-action pb-1">
                <v-spacer></v-spacer>
                <v-progress-linear class="mx-4" :indeterminate="true" v-if="loadingSelectAll"></v-progress-linear>
                <span v-else-if="showSelectAllToolbar">
                  All <strong>{{ pagination.rowsPerPage }}</strong> assessments on this page selected.
                  <a @click="selectAllAssessments">Select all {{ pagination.totalItems }} assessments corresponding to this search.</a>
                </span>
                <span v-else-if="selectedAllAssessments">
                  All <strong>{{ pagination.totalItems }}</strong> assessments corresponding to this search selected.
                  <a @click="clearSelection()">Clear selection</a>
                </span>
                <v-toolbar-items>
                  <v-dialog v-model="dialog" persistent max-width="500">
                    <template v-slot:activator="{ on }">
                      <v-btn small color="primary" dark v-on="on">
                        <v-icon>add</v-icon>
                          rescore
                      </v-btn>
                    </template>
                    <v-card>
                      <v-flex offset-xs1 pl-4>
                        <v-card-title class="headline">Select Rescoring Configuration</v-card-title>
                      </v-flex>
                      <v-flex sm7 offset-xs1>
                        <v-select v-model="modelAttribute"
                                  :items="modelAttributes"
                                  placeholder="Score Model">
                        </v-select>
                      </v-flex>
                      <v-flex sm7 offset-xs1>
                        <v-select v-model="versionAttribute"
                                  :items="versionAttributes"
                                  :disabled="!modelAttribute"
                                  placeholder="Scoring Model Version">
                        </v-select>
                      </v-flex>
                      <v-flex offset-xs6 pl-4 pb-5>
                        <v-btn color="secondary" @click="dialog = false">Cancel</v-btn>
                        <v-btn  class="ml-2" @click="sendRescoreRequest" color="primary" :disabled="!this.modelAttribute || !this.versionAttribute">Rescore</v-btn>
                      </v-flex>
                    </v-card>
                  </v-dialog>
                </v-toolbar-items>
              </v-toolbar>
              <v-data-table v-model="selectedAssessments"
                            :headers="$filterByEntitlements(tableHeaders)"
                            must-sort
                            show-select
                            no-data-text="No assessments correspond to your search"
                            :loading="loading"
                            :items="assessments"
                            :options.sync="pagination"
                            :server-items-length="pagination.totalItems"
                            class="elevation-1 accounts-table">
                <template slot="no-data">
                  <div class="text-xs-center py-2">
                      No results
                  </div>
                </template>
                <template slot="items" slot-scope="props">
                  <tr :active="props.selected">
                    <td>
                      <v-checkbox :input-value="props.selected"
                                  @change="props.selected = !props.selected"
                                  primary hide-details></v-checkbox>
                    </td>
                    <td> {{ props.item.id }} </td>
                    <td> {{ props.item.ats_profile_id }} </td>
                    <td> {{ props.item.user.username }} </td>
                    <td>
                      <template>
                        <tasks-list align :tasks="$store.getters['tasks/tasksList'](props.item.task_group_id)"></tasks-list>
                      </template>
                    </td>
                    <td style="text-align: left"> {{ props.item.assessment_group }} </td>
                  </tr>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
  </v-page>
</template>
<script>
import VPage from '@/layouts/Default'
import { searchAssessments, sendScoringRequest } from '@/services/Accounts'
import TasksList from '@/components/Tasks/TaskList'
import CurrentSession from '@/mixins/CurrentSession'
import { Bus } from '@/bus'
import RequestCancelToken from '@/mixins/RequestCancelToken'
import UrlParser from '@/core/parsers/Url.parser'

export default {
  name: 'AssessmentRescore',
  components: {
    VPage,
    TasksList
  },
  mixins: [ CurrentSession, RequestCancelToken ],
  data () {
    return {
      query: '',
      dialog: false,
      searchAttribute: 'assessment_groups',
      searchAttributeChoices: [
        { value: 'assessment_groups', text: 'Assessment Group' },
        { value: 'assessment_ids', text: 'Assessment Id' }
      ],
      versionAttribute: '',
      modelAttribute: '',
      modelAttributes: [],
      assessments: [],
      tableHeaders: [
        { text: 'Assessment Id', value: 'id', sortable: true },
        { text: 'ATS Profile Id', value: 'ats_profile_id', sortable: true },
        { text: 'Username', value: 'user.username', sortable: true },
        { text: 'Tasks', value: 'tasks', sortable: false },
        { text: 'Assessment Group', value: 'assessment_group', align: 'left', sortable: true }
      ],
      selectedAssessments: [],
      loadingSelectAll: false,
      selectedAllAssessments: false,
      allAssessmentsData: {},
      scoringModels: [],
      loading: false,
      pagination: {
        descending: true,
        rowsPerPage: 10,
        page: 1,
        totalItems: 0,
        sortBy: ['id']
      }
    }
  },
  computed: {
    showSelectAllToolbar () {
      return !this.selectedAllAssessments &&
        this.selectedAssessments.length === this.pagination.rowsPerPage &&
        this.selectedAssessments.length !== this.pagination.totalItems
    },
    searchParameters () {
      const query = {
        limit: this.pagination.rowsPerPage,
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        order: (this.pagination.descending ? '-' : '+') + this.pagination.sortBy
      }

      let attribute = this.searchAttribute

      if (attribute === 'id') {
        attribute = 'user_assessment_ids'
      }

      let queryStrings = this.query.split(',')
      for (let i = 0; i < queryStrings.length; i++) {
        let string = queryStrings[i]
        if (string.substring(0, 4) === 'http') {
          string = UrlParser.tokens(string)
        }
        queryStrings[i] = string.trim()
      }

      query[attribute] = queryStrings.join(',')

      return query
    },
    versionAttributes () {
      return this.scoringModels.filter((model) => {
        if (model.name === this.modelAttribute) {
          return true
        }
        return false
      }).map((model) => {
        return model.version
      })
    }
  },
  watch: {
    selectedAssessments () {
      this.selectedAllAssessments = false
    },
    pagination (newValue, oldValue) {
      this.searchAssessments()
    }
  },
  mounted () {
    this.fetchModels()
  },
  methods: {
    searchAssessments () {
      if (!this.query) {
        return
      }
      this.$set(this, 'loading', true)
      this.requestCancelTokenChecker()

      searchAssessments(this.searchParameters, this.requestToken.token)
        .then(({ assessments, total }) => {
          this.$set(this, 'loading', false)
          this.requestCancelTokenClearer()

          this.assessments = assessments
          this.pagination.totalItems = total
          this.selectedAllAssessments = false
        })
        .catch((error) => {
          if (error && error.message !== this.requestCancelMessage) {
            this.$set(this, 'loading', false)
            this.requestCancelTokenClearer()
            Bus.$emit('alert', ({ color: 'error', message: error }))
          }
        })
    },
    clearSelection () {
      this.selectedAssessments = []
      this.selectedAllAssessments = false
      this.versionAttribute = ''
      this.modelAttribute = ''
      this.dialog = false
    },
    fetchModels () {
      this.loading = true

      this.$store.dispatch('scores/fetchScoringModels')
        .then(response => {
          this.scoringModels = response
          this.modelAttributes = this.scoringModels.map(model => model.name)
        })
        .catch(() => {
          Bus.$emit('alert', { color: 'error', message: 'Failed to retrieve scoring models' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    search () {
      this.selectedAssessments = []
      if (!this.query || this.query.length === 0) {
        this.assessments = []
      } else {
        this.searchAssessments()
      }
    },
    selectAllAssessments () {
      this.loadingSelectAll = true
      searchAssessments({ ...this.searchParameters, limit: -1, offset: 0 })
        .then(({ assessments }) => {
          this.allAssessmentsData = assessments
          this.selectedAllAssessments = true
          this.loadingSelectAll = false
        })
    },
    sendRescoreRequest () {
      let assessmentIds = this.selectedAssessments
        .map((assessment) => {
          return assessment.id
        })

      sendScoringRequest(assessmentIds, { 'model_name': this.modelAttribute, 'model_version': this.versionAttribute })
        .then(response => {
          Bus.$emit('alert', { color: 'success', message: 'Successfully sent rescore request' })
        })
        .catch(() => {
          Bus.$emit('alert', { color: 'error', message: 'Failed to send rescore request' })
        })
        .finally(() => {
          this.loading = false
          this.clearSelection()
        })
    }
  }
}
</script>
<style lang="sass">
  .v-btn:not(.v-btn--round)
    max-height: 45px
</style>
